// @ts-nocheck
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - > */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { canUserSeePrices } from "@store/Framework/Store/Data/Accounts/AccountsSelectors";
import { css } from "styled-components";
/* <--- Absolunet Custom Code */

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // Store Custom Code
        canSeePrices: canUserSeePrices(state),
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // Starter Custom Code
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    orderNumberHeader?: DataTableHeaderProps;
    dateHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
    noOrdersFoundText: {
        forwardAs: "p",
    },
    /* Store Custom Code */
    shipToHeader: {
        css: css`
            width: 100%;
        `,
    },
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
        `,
    },
};

/* Starter Custom Code */
const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-text{
}`;
/* Starter Custom Code */

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    sort: "OrderDate DESC",
    customerSequence: "-1", // Absolunet Custom Fix
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        /* Starter Custom Code */
        const customCssWrapper = css`
            ${styles.wrapper?.css}
            ${this.props.fields.customCSS}
        `;

        const hasNoResults = !ordersDataView.value || ordersDataView.value.length === 0;

        return (
            <StyledWrapper {...styles.wrapper} css={customCssWrapper} data-test-selector="widget_RecentOrders">
                <CardListHeading heading={translate("Recent Orders")} viewAllUrl={orderHistoryUrl} />
                {ordersDataView.value.length === 0 && (
                    <Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>
                )}
                {/* Store Custom Code */}
                {ordersDataView.value.length > 0 && (
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader>{translate("Order #")}</DataTableHeader>
                                <DataTableHeader>{translate("Date Placed")}</DataTableHeader>
                                {this.props.canSeePrices /* Store Custom Code */ && (
                                    <DataTableHeader>{translate("Order Total")}</DataTableHeader>
                                )}
                                <DataTableHeader>{translate("Status")}</DataTableHeader>
                                {/* Store Custom Code - Start */}
                                <DataTableHeader {...styles.shipToHeader}>
                                    {translate("Ship To / Pickup")}
                                </DataTableHeader>
                                <DataTableHeader>{translate("Po #")}</DataTableHeader>
                                <DataTableHeader />
                                {/* Store Custom Code - End */}
                            </DataTableHead>
                            <DataTableBody>
                                {ordersDataView.value.map(
                                    order =>
                                        order !== null && (
                                            <OrderSummaryCard
                                                key={order.id}
                                                order={order}
                                                orderSettings={settingsCollection.orderSettings}
                                                extendedStyles={styles.orderSummaryCard}
                                            />
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
